/**
 * Metadata encryption/decryption for SkySend.
 *
 * Metadata (file names, sizes, MIME types) is encrypted with AES-256-GCM
 * using the metaKey derived from HKDF and a random 12-byte IV.
 *
 * The metadata is serialized as JSON, encoded to UTF-8, then encrypted.
 * The IV is stored separately (in the database) alongside the ciphertext.
 *
 * Security notes:
 * - A fresh random IV is generated for each metadata encryption
 * - The IV does not need to be secret, only unique per encryption
 * - GCM provides both confidentiality and authenticity
 */
import { randomBytes, asBytes } from "./util.js";
/** IV length for metadata encryption (12 bytes for AES-GCM). */
export const META_IV_LENGTH = 12;
/**
 * The block the metadata JSON is padded to with spaces, so the length of the encrypted blob
 * tells little about the names and types of the files in it. JSON allows the trailing spaces,
 * so every reader parses a padded blob like any other, older versions included.
 */
export const METADATA_PAD_BLOCK = 1024;
/**
 * The most bytes of encrypted metadata the server takes, the tag included, for a normal upload
 * and an upload into a file request alike. Base64 makes 100,000 characters of it.
 */
export const MAX_ENCRYPTED_METADATA_BYTES = 75_000;
/** The AES-GCM tag after the padded JSON. */
const META_TAG_LENGTH = 16;
function paddedLength(json) {
    return Math.max(1, Math.ceil(json.length / METADATA_PAD_BLOCK)) * METADATA_PAD_BLOCK;
}
/**
 * Whether a value is a byte count: a non-negative safe integer. JSON reads 1e999 as Infinity
 * and 1.5 as a float, and a hand-built blob can carry either.
 */
function isByteCount(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
const SUBMISSION = /^[0-9a-f]{32}$/;
/** The submission of parsed metadata, when it has one of the right form. Anything else is left out. */
function submissionOf(obj) {
    return typeof obj.submission === "string" && SUBMISSION.test(obj.submission)
        ? { submission: obj.submission }
        : {};
}
/**
 * Encrypt file metadata with AES-256-GCM, padded to whole METADATA_PAD_BLOCK blocks.
 *
 * @param metadata - The file metadata to encrypt
 * @param metaKey - The AES-256-GCM key derived for metadata
 * @returns The encrypted ciphertext and the random IV
 */
export async function encryptMetadata(metadata, metaKey) {
    const json = new TextEncoder().encode(JSON.stringify(metadata));
    const plaintext = new Uint8Array(paddedLength(json)).fill(0x20);
    plaintext.set(json);
    const iv = randomBytes(META_IV_LENGTH);
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: asBytes(iv), tagLength: META_TAG_LENGTH * 8 }, metaKey, asBytes(plaintext));
    return {
        ciphertext: new Uint8Array(ciphertext),
        iv,
    };
}
/**
 * The length of the ciphertext encryptMetadata makes of this metadata. A client checks it
 * against MAX_ENCRYPTED_METADATA_BYTES before an upload, since the server only gets the
 * metadata once the file is up.
 */
export function encryptedMetadataLength(metadata) {
    return paddedLength(new TextEncoder().encode(JSON.stringify(metadata))) + META_TAG_LENGTH;
}
/**
 * Decrypt file metadata from AES-256-GCM ciphertext.
 *
 * @param ciphertext - The encrypted metadata
 * @param iv - The IV used during encryption
 * @param metaKey - The AES-256-GCM key derived for metadata
 * @returns The decrypted and parsed file metadata
 * @throws If decryption fails (wrong key, tampered data) or JSON is invalid
 */
export async function decryptMetadata(ciphertext, iv, metaKey) {
    return validateMetadata(await decryptJson(ciphertext, iv, metaKey));
}
/**
 * Decrypt the metadata of an upload into a file request, which may also be a note. Only the
 * inbox calls this.
 */
export async function decryptRequestMetadata(ciphertext, iv, metaKey) {
    const data = await decryptJson(ciphertext, iv, metaKey);
    if (typeof data === "object" && data !== null && data.type === "note") {
        const { size } = data;
        if (!isByteCount(size)) {
            throw new Error("Invalid metadata: invalid note size");
        }
        return { type: "note", size, ...submissionOf(data) };
    }
    return validateMetadata(data);
}
async function decryptJson(ciphertext, iv, metaKey) {
    if (iv.length !== META_IV_LENGTH) {
        throw new Error(`Metadata IV must be exactly ${META_IV_LENGTH} bytes`);
    }
    let plaintext;
    try {
        plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: asBytes(iv), tagLength: 128 }, metaKey, asBytes(ciphertext));
    }
    catch {
        throw new Error("Metadata decryption failed - data may be corrupted or tampered with");
    }
    const decoder = new TextDecoder();
    const json = decoder.decode(plaintext);
    try {
        return JSON.parse(json);
    }
    catch {
        throw new Error("Metadata decryption produced invalid JSON");
    }
}
/**
 * The byte size the decrypted file stream must have, to pass to `createDecryptStream`.
 *
 * GCM authenticates each record on its own, so a server can drop whole records from
 * the end of the stream and the rest still decrypts. The metadata is authenticated
 * with the metaKey, so its size is the one the server cannot change. Undefined only
 * for archives uploaded by older clients, which carry no archive size.
 */
export function expectedPlaintextSize(metadata) {
    return metadata.type === "archive" ? metadata.archiveSize : metadata.size;
}
/** Validate that parsed JSON conforms to the FileMetadata shape. */
function validateMetadata(data) {
    if (typeof data !== "object" || data === null) {
        throw new Error("Invalid metadata: not an object");
    }
    const obj = data;
    if (obj.type === "single") {
        if (typeof obj.name !== "string" || obj.name.length === 0) {
            throw new Error("Invalid metadata: missing or empty file name");
        }
        if (!isByteCount(obj.size)) {
            throw new Error("Invalid metadata: invalid file size");
        }
        if (typeof obj.mimeType !== "string") {
            throw new Error("Invalid metadata: missing MIME type");
        }
        return {
            type: "single",
            name: obj.name,
            size: obj.size,
            mimeType: obj.mimeType,
            ...submissionOf(obj),
        };
    }
    if (obj.type === "archive") {
        if (!Array.isArray(obj.files)) {
            throw new Error("Invalid metadata: files must be an array");
        }
        const files = [];
        for (const file of obj.files) {
            if (typeof file !== "object" || file === null) {
                throw new Error("Invalid metadata: file entry must be an object");
            }
            const f = file;
            if (typeof f.name !== "string" || f.name.length === 0) {
                throw new Error("Invalid metadata: file entry missing name");
            }
            if (!isByteCount(f.size)) {
                throw new Error("Invalid metadata: file entry invalid size");
            }
            files.push({ name: f.name, size: f.size });
        }
        if (!isByteCount(obj.totalSize)) {
            throw new Error("Invalid metadata: invalid total size");
        }
        if (obj.archiveSize !== undefined && !isByteCount(obj.archiveSize)) {
            throw new Error("Invalid metadata: invalid archive size");
        }
        return {
            type: "archive",
            files,
            totalSize: obj.totalSize,
            ...(obj.archiveSize !== undefined ? { archiveSize: obj.archiveSize } : {}),
            ...submissionOf(obj),
        };
    }
    throw new Error("Invalid metadata: unknown type");
}
//# sourceMappingURL=metadata.js.map